import { lazy, Suspense, useState, useEffect } from 'react';
import { PHASE } from './state/phases.js';
import { PAGE } from './state/pages.js';
import { GAME_MODE, DEFAULT_GAME_MODE } from './data/modes.js';
import { TitleScreen } from './screens/TitleScreen.jsx';
import { SubsidyRevealScreen } from './screens/SubsidyRevealScreen.jsx';
import { BuildScreen } from './screens/BuildScreen.jsx';
import { GridPerformanceReportScreen } from './screens/GridPerformanceReportScreen.jsx';
import { Cop30Screen } from './screens/Cop30Screen.jsx';
import { FinalScoreScreen } from './screens/FinalScoreScreen.jsx';
import { RulesScreen } from './screens/RulesScreen.jsx';
import { TitlePage } from './pages/TitlePage.jsx';
import { HomePage } from './pages/HomePage.jsx';
import { NameEntryPage } from './pages/NameEntryPage.jsx';
import { CountryEntryPage } from './pages/CountryEntryPage.jsx';
import { RoleEntryPage } from './pages/RoleEntryPage.jsx';
import { HighScoresPage } from './pages/HighScoresPage.jsx';
import { GameView } from './pages/GameView.jsx';
import { AudioProvider, useAudio } from './audio/AudioProvider.jsx';
import { AudioSettings } from './audio/AudioSettings.jsx';
import { scrollToTop } from './components/useFocusOnMount.js';
import {
  FINAL_SCORE_IMAGE_URLS,
  decodeImages,
  preloadGameImages,
  warmDecodeGameImages
} from './utils/preloadImages.js';

const OptimisationLabPage = lazy(() =>
  import('../optimisation-lab/components/OptimisationLab.jsx')
);

async function requestLandscapeForGame() {
  if (typeof window === 'undefined') return;
  if (!window.matchMedia?.('(pointer: coarse)').matches) return;
  if (window.innerWidth > 900) return;

  try {
    if (document.fullscreenEnabled && !document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    }
    await window.screen?.orientation?.lock?.('landscape');
  } catch {
    // Browsers that block orientation locking still use the CSS landscape stage.
  }
}

export function renderScreen({ state, dispatch, onShowRules }) {
  switch (state.phase) {
    case PHASE.AWAITING_START:
      return <TitleScreen dispatch={dispatch} onShowRules={onShowRules} />;
    case PHASE.AWAITING_SUBSIDY_REVEAL:
      return <SubsidyRevealScreen state={state} dispatch={dispatch} />;
    case PHASE.BUILDING:
      return <BuildScreen state={state} dispatch={dispatch} />;
    case PHASE.AWAITING_VARIABLE_ROLLS:
    case PHASE.AWAITING_DISASTER:
    case PHASE.RESOLVING:
      return null;
    case PHASE.BETWEEN_YEARS:
      return <GridPerformanceReportScreen state={state} dispatch={dispatch} />;
    case PHASE.COP30:
      return <Cop30Screen state={state} dispatch={dispatch} />;
    case PHASE.GAME_OVER:
      return <FinalScoreScreen state={state} dispatch={dispatch} />;
    default:
      return null;
  }
}

function AppContent() {
  // Page routing
  const [page, setPage] = useState(PAGE.TITLE);
  const [highScoresReturnPage, setHighScoresReturnPage] = useState(PAGE.HOME);

  // Planner identity with localStorage persistence
  const [playerName, setPlayerName] = useState(() => {
    const stored = localStorage.getItem('playerName');
    return stored || '';
  });
  const [playerCountry, setPlayerCountry] = useState(() => {
    const stored = localStorage.getItem('playerCountry');
    return stored || '';
  });
  const [playerRole, setPlayerRole] = useState(() => {
    const stored = localStorage.getItem('playerRole');
    return stored || '';
  });
  const [gameMode, setGameMode] = useState(() => {
    const stored = localStorage.getItem('gameMode');
    return stored === GAME_MODE.ADVANCED_CAPACITY
      ? GAME_MODE.ADVANCED_CAPACITY
      : DEFAULT_GAME_MODE;
  });

  // Persist planner identity to localStorage
  useEffect(() => {
    localStorage.setItem('playerName', playerName);
  }, [playerName]);

  useEffect(() => {
    localStorage.setItem('playerCountry', playerCountry);
  }, [playerCountry]);

  useEffect(() => {
    localStorage.setItem('playerRole', playerRole);
  }, [playerRole]);

  useEffect(() => {
    localStorage.setItem('gameMode', gameMode);
  }, [gameMode]);

  // Warm the image cache on first mount so cards render without a fetch delay later.
  useEffect(() => {
    preloadGameImages();
    decodeImages(FINAL_SCORE_IMAGE_URLS);
    warmDecodeGameImages();
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [page]);

  const { setMusicAllowed } = useAudio();

  useEffect(() => {
    setMusicAllowed(page !== PAGE.TITLE);
  }, [page, setMusicAllowed]);

  // Wire up browser back/forward for page navigation
  useEffect(() => {
    function handlePopState() {
      const previousPage = sessionStorage.getItem('currentPage');
      if (previousPage) {
        setPage(previousPage);
      }
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Push page to history when navigating
  const handleNavigate = (newPage, options = {}) => {
    if (newPage === PAGE.GAME) {
      void requestLandscapeForGame();
    }
    if (
      newPage === PAGE.DAILY_HIGH_SCORES ||
      newPage === PAGE.ALL_TIME_HIGH_SCORES ||
      newPage === PAGE.HIGH_SCORES
    ) {
      setHighScoresReturnPage(options.returnTo ?? PAGE.HOME);
    }
    sessionStorage.setItem('currentPage', newPage);
    window.history.pushState({ page: newPage }, '');
    setPage(newPage);
  };

  const [showRules, setShowRules] = useState(false);
  const highScoresOpenFromGame =
    highScoresReturnPage === PAGE.GAME &&
    [
      PAGE.HIGH_SCORES,
      PAGE.DAILY_HIGH_SCORES,
      PAGE.ALL_TIME_HIGH_SCORES
    ].includes(page);

  // Determine if we're in onboarding (hide header) or game (show header)
  const isOnboarding = [
    PAGE.TITLE,
    PAGE.HOME,
    PAGE.SETUP_NAME,
    PAGE.SETUP_COUNTRY,
    PAGE.SETUP_ROLE,
    PAGE.HIGH_SCORES,
    PAGE.DAILY_HIGH_SCORES,
    PAGE.ALL_TIME_HIGH_SCORES
  ].includes(page);
  const usesFixedLandscape = page === PAGE.GAME;
  const appClassName = usesFixedLandscape ? 'app app-fixed-landscape' : 'app';

  useEffect(() => {
    document.documentElement.classList.toggle('game-fixed-landscape-root', usesFixedLandscape);
    document.body.classList.toggle('game-fixed-landscape-body', usesFixedLandscape);

    return () => {
      document.documentElement.classList.remove('game-fixed-landscape-root');
      document.body.classList.remove('game-fixed-landscape-body');
    };
  }, [usesFixedLandscape]);

  // The title screen is rendered outside the .app shell so it gets a
  // clean, fullscreen viewport (no flex parent, no skip-link, no header).
  if (page === PAGE.TITLE) {
    return <TitlePage onNavigate={handleNavigate} />;
  }

  if (page === PAGE.OPTIMISATION_LAB) {
    return (
      <Suspense
        fallback={(
          <div className="app lab-app-shell">
            <main id="lab-main" className="optimisation-lab">
              <p role="status">Loading Optimisation Lab.</p>
            </main>
          </div>
        )}
      >
        <OptimisationLabPage onHome={() => handleNavigate(PAGE.HOME)} />
      </Suspense>
    );
  }

  return (
    <div className={appClassName}>
      <a className="skip-link" href="#main">Skip to main content</a>

      {/* App header: hide during onboarding, show during game */}
      {!isOnboarding && (
        <header className="app-header" role="banner">
          <div className="app-header-inner">
            <button
              type="button"
              className="app-header-home"
              onClick={() => handleNavigate(PAGE.HOME)}
              data-audio-cue="buttonPrimary"
              aria-label="Return to menu"
              title="Return to menu"
              data-testid="header-home"
            >
              <svg
                className="app-header-home-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M4 11.5 12 4l8 7.5" />
                <path d="M6.5 10.5V20h11v-9.5" />
                <path d="M10 20v-5h4v5" />
              </svg>
            </button>
            <AudioSettings />
            <p className="app-eyebrow">Power Grid Optimisation</p>
            <h1 className="app-title">
              Shortfall
              <span className="app-title-bolt" aria-hidden="true">
                {'\u26a1'}
              </span>
            </h1>
            <button
              type="button"
              className="app-header-rules"
              onClick={() => setShowRules(true)}
              aria-label="Open how-to-play reference"
              data-testid="header-rules"
              data-audio-cue="buttonSecondary"
            >
              How to play
            </button>
          </div>
        </header>
      )}

      {/* Page routing */}
      {page === PAGE.HOME ? (
        <HomePage
          onNavigate={handleNavigate}
          gameMode={gameMode}
          onGameModeChange={setGameMode}
        />
      ) : page === PAGE.SETUP_NAME ? (
        <NameEntryPage
          playerName={playerName}
          onNameChange={setPlayerName}
          onNavigate={handleNavigate}
        />
      ) : page === PAGE.SETUP_COUNTRY ? (
        <CountryEntryPage
          playerCountry={playerCountry}
          onCountryChange={setPlayerCountry}
          onNavigate={handleNavigate}
        />
      ) : page === PAGE.SETUP_ROLE ? (
        <RoleEntryPage
          playerRole={playerRole}
          onRoleChange={setPlayerRole}
          onNavigate={handleNavigate}
        />
      ) : null}

      {page === PAGE.GAME || highScoresOpenFromGame ? (
        <div
          className="game-page-stage"
          hidden={page !== PAGE.GAME}
          aria-hidden={page !== PAGE.GAME ? 'true' : undefined}
        >
          <GameView
            key={gameMode}
            playerName={playerName}
            playerCountry={playerCountry}
            playerRole={playerRole}
            gameMode={gameMode}
            rulesOpen={showRules}
            onRulesClose={() => setShowRules(false)}
            onNavigate={handleNavigate}
          />
        </div>
      ) : null}

      {page === PAGE.DAILY_HIGH_SCORES ? (
        <HighScoresPage
          board="daily"
          gameMode={gameMode}
          onNavigate={handleNavigate}
          returnPage={highScoresReturnPage}
        />
      ) : page === PAGE.ALL_TIME_HIGH_SCORES ? (
        <HighScoresPage
          board="allTime"
          gameMode={gameMode}
          onNavigate={handleNavigate}
          returnPage={highScoresReturnPage}
        />
      ) : page === PAGE.HIGH_SCORES ? (
        <HighScoresPage
          board="daily"
          gameMode={gameMode}
          onNavigate={handleNavigate}
          returnPage={highScoresReturnPage}
        />
      ) : null}

      {/* Footer: hide during onboarding */}
      {!isOnboarding && (
        <footer className="app-footer" role="contentinfo">
          <p>Source of truth: v2.4 (Release version: 1.0).</p>
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <AudioProvider>
      <AppContent />
    </AudioProvider>
  );
}

export default App;
